import { DEFAULT_DURABLE_LOG_CONFIG, DurableLogConfig } from "./config/durableLogConfig";
import { CoverageTracker } from "./coverage/CoverageTracker";
import { BATCH_REPROCESS_TTL_MS } from "../batchReprocess";
import {
  cancelConsultationDbDrop,
  ConsultationDexieDb,
  dropConsultationDb,
  getConsultationDb,
  scheduleConsultationDbDrop,
} from "./db/ConsultationDexieDb";
import { AudioSegmentRepo } from "./domain/AudioSegmentRepo";
import { RecoveryCheckpointRepo } from "./domain/RecoveryCheckpointRepo";
import { SessionManifestRepo } from "./domain/SessionManifestRepo";
import {
  AudioSegment,
  CaptureChunk,
  ConsultationStorageStatus,
  ReplayBatch,
  ReplayPlan,
  SessionManifest,
} from "./domain/types";
import { ActiveSegmentBuilder } from "./ingest/ActiveSegmentBuilder";
import { ReplayEdgeRing } from "./ingest/ReplayEdgeRing";
import { IdbPayloadStore } from "./payload/IdbPayloadStore";
import { FinalArchiveBuilder } from "./archive/FinalArchiveBuilder";
import { RetentionManager } from "./retention/RetentionManager";
import { RecoveryPlanner } from "./recovery/RecoveryPlanner";
import { TranscriptMerger, TranscriptMergeResult } from "./transcript/TranscriptMerger";
import { RecoveryDiagnostics } from "./diagnostics/RecoveryDiagnostics";

export const CONSULTATION_DB_TTL_MS = BATCH_REPROCESS_TTL_MS;
const ENABLE_CONSULTATION_STORAGE_RESET_LOGGING = false;

const createSessionId = (): string =>
  [
    "session",
    Date.now().toString(36),
    Math.random().toString(36).slice(2, 10),
  ].join("-");

const checksum32 = (bytes: Uint8Array): number => {
  let hash = 0;

  for (let index = 0; index < bytes.byteLength; index += 1) {
    hash = (hash + bytes[index]) >>> 0;
  }

  return hash >>> 0;
};

const bytesFromArrayBuffer = (payload: ArrayBuffer): Uint8Array => {
  const input = new Uint8Array(payload);
  const copy = new Uint8Array(input.byteLength);
  copy.set(input);
  return copy;
};

const toArrayBuffer = (bytes: Uint8Array): ArrayBuffer => {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return copy.buffer;
};

export interface DurableAudioSessionManagerOptions {
  sessionId?: string;
  config?: Partial<DurableLogConfig>;
  persistenceGranted?: boolean;
  onDiagnostic?: (detail: Record<string, unknown>) => void;
}

export interface ConsultationStorageStatusOptions {
  initializeIfMissing?: boolean;
}

export class DurableAudioSessionManager {
  private db: ConsultationDexieDb;
  private readonly config: DurableLogConfig;
  private readonly sessionId: string;
  private manifestRepo: SessionManifestRepo;
  private segmentRepo: AudioSegmentRepo;
  private checkpointRepo: RecoveryCheckpointRepo;
  private payloadStore: IdbPayloadStore;
  private readonly segmentBuilder = new ActiveSegmentBuilder();
  private readonly replayEdgeRing: ReplayEdgeRing;
  private readonly coverageTracker: CoverageTracker;
  private readonly recoveryPlanner: RecoveryPlanner;
  private readonly transcriptMerger = new TranscriptMerger();
  private retentionManager: RetentionManager;
  private archiveBuilder: FinalArchiveBuilder;
  private diagnostics: RecoveryDiagnostics;

  private manifest: SessionManifest | null = null;
  private nextSegmentId = 1;
  private capturedTimelineMs = 0;
  private lastFlushAt: number | null = null;
  private appendChain: Promise<void> = Promise.resolve();
  private persistenceQueue: Promise<void> = Promise.resolve();
  private manifestPersistInFlight: Promise<void> | null = null;
  private manifestPersistRequested = false;
  private flushInFlight: Promise<void> | null = null;

  constructor(private readonly options: DurableAudioSessionManagerOptions = {}) {
    this.db = getConsultationDb();
    this.config = {
      ...DEFAULT_DURABLE_LOG_CONFIG,
      ...options.config,
    };
    this.sessionId = options.sessionId ?? createSessionId();
    this.manifestRepo = new SessionManifestRepo(this.db);
    this.segmentRepo = new AudioSegmentRepo(this.db);
    this.checkpointRepo = new RecoveryCheckpointRepo(this.db);
    this.payloadStore = new IdbPayloadStore(this.db);
    this.replayEdgeRing = new ReplayEdgeRing(this.config.replayOverlapMs);
    this.coverageTracker = new CoverageTracker({
      partialStabilityMinCount: this.config.partialStabilityMinCount,
      partialStabilityMinMs: this.config.partialStabilityMinMs,
    });
    this.recoveryPlanner = new RecoveryPlanner({
      replayOverlapMs: this.config.replayOverlapMs,
      replayMode: this.config.replayMode,
    });
    this.retentionManager = new RetentionManager(
      this.segmentRepo,
      this.manifestRepo,
      this.payloadStore
    );
    this.archiveBuilder = new FinalArchiveBuilder(this.payloadStore);
    this.diagnostics = this.createDiagnostics();
  }

  async startSession(now = Date.now()): Promise<void> {
    cancelConsultationDbDrop();
    await this.resetConsultationStorage();
    await this.logConsultationStorageReset();

    const createdAt = now;
    this.manifest = {
      sessionId: this.sessionId,
      streamEpoch: createdAt.toString(),
      startedAt: createdAt,
      sampleRate: 16000,
      channels: 1,
      encoding: "pcm_s16le",
      totalSegments: 0,
      totalBytes: 0,
      totalDurationMs: 0,
      lastAssignedSeq: 0,
      lastDurableSeq: 0,
      lastSentSeq: 0,
      hardCommittedMs: 0,
      softCommittedMs: 0,
      replayFromSeq: 0,
      replayInFlight: false,
      reconnectCount: 0,
      archiveState: "capturing",
      storagePersistenceGranted: this.options.persistenceGranted,
      createdAt,
      updatedAt: createdAt,
    };

    await this.enqueuePersistenceTask(async () => {
      await this.manifestRepo.upsert(this.manifest as SessionManifest);
      await this.checkpointRepo.upsert({
        sessionId: this.sessionId,
        hardCommittedMs: 0,
        softCommittedMs: 0,
        lastFinalCoverageMs: 0,
        lastStablePartialCoverageMs: 0,
      });
    });
  }

  async appendCaptureChunk(
    payload: ArrayBuffer,
    durationMs: number,
    now = Date.now()
  ): Promise<number> {
    const appendOperation = this.appendChain.then(() =>
      this.appendCaptureChunkInternal(payload, durationMs, now)
    );
    this.appendChain = appendOperation.then(
      () => undefined,
      () => undefined
    );

    return appendOperation;
  }

  private async appendCaptureChunkInternal(
    payload: ArrayBuffer,
    durationMs: number,
    now: number
  ): Promise<number> {
    await this.ensureSession();

    if (!this.manifest) {
      return 0;
    }

    await this.applyBackpressureIfNeeded(payload.byteLength, now);

    const seq = this.manifest.lastAssignedSeq + 1;
    const startMs = this.capturedTimelineMs;
    const safeDurationMs = Math.max(durationMs, 0);
    const endMs = startMs + safeDurationMs;
    this.capturedTimelineMs = endMs;

    const bytes = bytesFromArrayBuffer(payload);
    const chunk: CaptureChunk = {
      seq,
      chunkId: `${this.sessionId}:${seq}`,
      startMs,
      endMs,
      durationMs: safeDurationMs,
      bytes,
    };

    this.manifest.lastAssignedSeq = seq;
    this.manifest.updatedAt = now;

    this.replayEdgeRing.append(chunk);
    this.segmentBuilder.append(chunk);

    if (
      this.segmentBuilder.shouldFlush(now, {
        maxBytes: this.config.segmentFlushBytes,
        maxDurationMs: this.config.segmentFlushDurationMs,
        flushIntervalMs: this.config.segmentFlushIntervalMs,
        lastFlushAt: this.lastFlushAt,
      })
    ) {
      await this.flush("threshold", now);
      return seq;
    }

    if (seq % 10 === 0) {
      await this.persistManifest();
    }

    return seq;
  }

  async flush(reason: "threshold" | "timer" | "stop" | "recovery", now = Date.now()): Promise<void> {
    await this.ensureSession();

    if (!this.manifest) {
      return;
    }

    if (this.flushInFlight) {
      await this.flushInFlight;
    }

    const flushPromise = this.flushInternal(reason, now);
    this.flushInFlight = flushPromise;

    try {
      await flushPromise;
    } finally {
      if (this.flushInFlight === flushPromise) {
        this.flushInFlight = null;
      }
    }
  }

  private async flushInternal(
    reason: "threshold" | "timer" | "stop" | "recovery",
    now: number
  ): Promise<void> {
    if (!this.manifest) {
      return;
    }

    const builtSegment = this.segmentBuilder.flush(this.nextSegmentId);

    if (!builtSegment) {
      return;
    }

    const payloadKey = `${this.sessionId}:${builtSegment.segmentId}`;
    const createdAt = now;

    const segment: AudioSegment = {
      sessionId: this.sessionId,
      segmentId: builtSegment.segmentId,
      startSeq: builtSegment.startSeq,
      endSeq: builtSegment.endSeq,
      startMs: builtSegment.startMs,
      endMs: builtSegment.endMs,
      durationMs: builtSegment.durationMs,
      byteLength: builtSegment.bytes.byteLength,
      chunkCount: builtSegment.chunkCount,
      payloadKey,
      storageKind: "idb",
      checksum32: checksum32(builtSegment.bytes),
      keepForReplay: true,
      keepForArchive: true,
      createdAt,
    };

    const nextManifest: SessionManifest = {
      ...this.manifest,
      totalSegments: this.manifest.totalSegments + 1,
      totalBytes: this.manifest.totalBytes + builtSegment.bytes.byteLength,
      totalDurationMs: Math.max(this.manifest.totalDurationMs, builtSegment.endMs),
      lastDurableSeq: builtSegment.endSeq,
      updatedAt: createdAt,
    };

    await this.enqueuePersistenceTask(async () => {
      await (this.db.transaction as any)(
        "rw",
        this.db.manifests,
        this.db.segments,
        this.db.payloads,
        this.db.chunkIndex,
        async () => {
          await this.db.payloads.put({
            payloadKey,
            sessionId: this.sessionId,
            segmentId: builtSegment.segmentId,
            byteLength: builtSegment.bytes.byteLength,
            checksum32: segment.checksum32,
            payload: new Blob([toArrayBuffer(builtSegment.bytes)], {
              type: "application/octet-stream",
            }),
            createdAt,
          });

          await this.db.segments.put(segment);

          if (this.config.chunkIndexEnabled) {
            const chunkRows = builtSegment.chunkIndexRows.map((row) => ({
              ...row,
              sessionId: this.sessionId,
            }));
            if (chunkRows.length > 0) {
              await this.db.chunkIndex.bulkPut(chunkRows);
            }
          }

          await this.db.manifests.put(nextManifest);
        }
      );
    });

    this.manifest = nextManifest;
    this.nextSegmentId += 1;
    this.lastFlushAt = now;
    await this.diagnostics.record("segment_flushed", {
      reason,
      segmentId: segment.segmentId,
      byteLength: segment.byteLength,
      endSeq: segment.endSeq,
    });
  }

  async onTranscriptMessage(message: unknown, now = Date.now()): Promise<void> {
    await this.ensureSession();

    if (!this.manifest) {
      return;
    }

    const coverage = this.coverageTracker.onTranscriptMessage(message, now);

    if (!coverage.updated) {
      return;
    }

    this.manifest.hardCommittedMs = Math.max(
      this.manifest.hardCommittedMs,
      coverage.hardCommittedMs
    );
    this.manifest.softCommittedMs = Math.max(
      this.manifest.softCommittedMs,
      coverage.softCommittedMs
    );
    this.manifest.lastCommitAdvanceAt = coverage.lastCommitAdvanceAt ?? now;
    this.manifest.updatedAt = now;

    await this.enqueuePersistenceTask(async () => {
      await this.db.transaction(
        "rw",
        this.db.manifests,
        this.db.recoveryCheckpoints,
        async () => {
          await this.db.manifests.put(this.manifest as SessionManifest);
          await this.db.recoveryCheckpoints.put({
            sessionId: this.sessionId,
            hardCommittedMs: this.manifest?.hardCommittedMs ?? 0,
            softCommittedMs: this.manifest?.softCommittedMs ?? 0,
            lastFinalCoverageMs: coverage.lastFinalCoverageMs,
            lastStablePartialCoverageMs: coverage.lastStablePartialCoverageMs,
            lastCommitAdvanceAt: coverage.lastCommitAdvanceAt ?? undefined,
          });
        }
      );
    });

    await this.retentionManager.markReplaySafeThroughMs(
      this.sessionId,
      this.manifest.hardCommittedMs
    );
  }

  mergeTranscriptMessage(message: {
    is_partial?: boolean;
    data?: { text?: string };
  }): TranscriptMergeResult {
    const text = message?.data?.text;

    if (typeof text !== "string") {
      return { dedupedChars: 0 };
    }

    if (message.is_partial) {
      return this.transcriptMerger.ingestPartial(text);
    }

    return this.transcriptMerger.ingestFinal(text);
  }

  async markLiveSentThroughSeq(seq: number, now = Date.now()): Promise<void> {
    await this.ensureSession();

    if (!this.manifest || seq <= this.manifest.lastSentSeq) {
      return;
    }

    this.manifest.lastSentSeq = seq;
    this.manifest.updatedAt = now;
    await this.persistManifest();
  }

  async incrementReconnectCount(now = Date.now()): Promise<void> {
    await this.ensureSession();

    if (!this.manifest) {
      return;
    }

    this.manifest.reconnectCount += 1;
    this.manifest.updatedAt = now;
    await this.persistManifest();
  }

  async prepareReplayPlan(): Promise<ReplayPlan | null> {
    await this.ensureSession();

    if (!this.manifest || this.manifest.lastDurableSeq <= 0) {
      return null;
    }

    const committedMs = this.recoveryPlanner.getCommittedMs(this.manifest);
    const replayFromMs = Math.max(committedMs - this.config.replayOverlapMs, 0);
    const replayFromSeq =
      (await this.segmentRepo.getFirstSeqAtOrAfterMs(this.sessionId, replayFromMs)) ??
      this.manifest.lastDurableSeq + 1;

    if (replayFromSeq > this.manifest.lastDurableSeq) {
      return null;
    }

    this.manifest.replayFromSeq = replayFromSeq;
    this.manifest.replayInFlight = true;
    this.manifest.updatedAt = Date.now();
    await this.persistManifest();

    await this.diagnostics.record("replay_plan_created", {
      replayFromMs,
      replayFromSeq,
      committedMs,
    });

    return {
      committedMs,
      replayFromMs,
      replayFromSeq,
    };
  }

  async getReplayBatch(fromSeq: number): Promise<ReplayBatch> {
    await this.ensureSession();

    if (!this.manifest) {
      return {
        segments: [],
        payloads: [],
        nextSeq: fromSeq,
        done: true,
      };
    }

    const segments = await this.segmentRepo.getReplayBatch(
      this.sessionId,
      fromSeq,
      this.config.replayBatchMaxSegments,
      this.config.replayBatchMaxBytes
    );

    if (segments.length === 0) {
      return {
        segments: [],
        payloads: [],
        nextSeq: fromSeq,
        done: true,
      };
    }

    const payloads: Array<{ segmentId: number; payload: ArrayBuffer }> = [];

    for (let index = 0; index < segments.length; index += 1) {
      const segment = segments[index];
      const payload = await this.payloadStore.readSegment(segment.payloadKey);
      payloads.push({
        segmentId: segment.segmentId,
        payload,
      });
    }

    const lastSegment = segments[segments.length - 1];
    const nextSeq = lastSegment.endSeq + 1;

    return {
      segments,
      payloads,
      nextSeq,
      done: nextSeq > this.manifest.lastDurableSeq,
    };
  }

  async completeReplayCycle(now = Date.now()): Promise<void> {
    await this.ensureSession();

    if (!this.manifest) {
      return;
    }

    this.manifest.replayInFlight = false;
    this.manifest.updatedAt = now;
    await this.persistManifest();
  }

  async finalizeSession(now = Date.now()): Promise<SessionManifest> {
    await this.ensureSession();
    await this.flush("stop", now);

    if (!this.manifest) {
      throw new Error("Unable to finalize durable session state.");
    }

    this.manifest.archiveState = "ready_to_upload";
    this.manifest.endedAt = now;
    this.manifest.updatedAt = now;
    await this.persistManifest();

    await this.diagnostics.record("session_finalized", {
      totalSegments: this.manifest.totalSegments,
      totalBytes: this.manifest.totalBytes,
      totalDurationMs: this.manifest.totalDurationMs,
    });

    return this.manifest;
  }

  async buildFinalUpload(
    format: "pcm" | "wav" = "pcm"
  ): Promise<Blob | ReadableStream<Uint8Array>> {
    if (!this.manifest) {
      throw new Error("No durable session to export.");
    }

    await this.flush("stop");
    const segments = await this.segmentRepo.listBySession(this.sessionId);

    if (segments.length === 0) {
      throw new Error("No durable consultation audio available for export.");
    }

    if (format === "wav") {
      return this.archiveBuilder.buildWavBlob(segments, this.manifest.totalBytes);
    }

    const stream = this.archiveBuilder.buildPcmStream(segments);

    if (stream) {
      return stream;
    }

    return this.archiveBuilder.buildPcmBlob(segments);
  }

  async markArchiveCommitted(uploadId: string, now = Date.now()): Promise<void> {
    await this.ensureSession();

    if (!this.manifest) {
      return;
    }

    this.manifest.archiveState = "committed";
    this.manifest.archiveUploadId = uploadId;
    this.manifest.archiveCommittedAt = now;
    this.manifest.updatedAt = now;

    await this.retentionManager.markArchiveCommitted(this.sessionId, uploadId, now);
    await this.diagnostics.record("archive_committed", {
      uploadId,
      at: now,
    });
  }

  async compactIfEligible(): Promise<number> {
    await this.ensureSession();

    if (!this.manifest || this.manifest.replayInFlight || this.manifest.archiveState !== "committed") {
      return 0;
    }

    const deleted = await this.retentionManager.compactIfEligible(this.sessionId);

    if (deleted > 0) {
      await this.diagnostics.record("compaction_completed", {
        deleted,
      });
    }

    return deleted;
  }

  async getConsultationStorageStatus(
    options: ConsultationStorageStatusOptions = {}
  ): Promise<ConsultationStorageStatus | null> {
    if (!this.manifest) {
      if (options.initializeIfMissing === false) {
        return null;
      }

      await this.ensureSession();
    }

    if (!this.manifest) {
      return null;
    }

    const committedMs = this.recoveryPlanner.getCommittedMs(this.manifest);
    const replayFromMs = Math.max(committedMs - this.config.replayOverlapMs, 0);
    const replayFromSeq =
      this.manifest.replayFromSeq ??
      (await this.segmentRepo.getFirstSeqAtOrAfterMs(this.sessionId, replayFromMs)) ??
      0;

    const backlogMs = Math.max(this.manifest.totalDurationMs - committedMs, 0);
    const backlogBytes = await this.estimateBacklogBytes(committedMs);

    return {
      sessionId: this.sessionId,
      archiveState: this.manifest.archiveState,
      durableSegments: this.manifest.totalSegments,
      durableBytes: this.manifest.totalBytes,
      totalDurationMs: this.manifest.totalDurationMs,
      hardCommittedMs: this.manifest.hardCommittedMs,
      softCommittedMs: this.manifest.softCommittedMs,
      replayFromMs,
      replayFromSeq,
      replayInFlight: this.manifest.replayInFlight,
      backlogMs,
      backlogBytes,
      lastDurableSeq: this.manifest.lastDurableSeq,
      lastAssignedSeq: this.manifest.lastAssignedSeq,
      lastSentSeq: this.manifest.lastSentSeq,
      reconnectCount: this.manifest.reconnectCount,
      updatedAt: this.manifest.updatedAt,
    };
  }

  async getDiagnostics(): Promise<Record<string, unknown>> {
    const inMemory = this.diagnostics.getInMemorySnapshot();
    const persistent = await this.diagnostics.getPersistentSnapshot();

    return {
      sessionId: this.sessionId,
      inMemory,
      persistent,
      dedupedChars: this.transcriptMerger.getDedupedChars(),
    };
  }

  getSessionId(): string {
    return this.sessionId;
  }

  getCoverageState() {
    return this.coverageTracker.getState();
  }

  scheduleStorageDropAfterStop(ttlMs = CONSULTATION_DB_TTL_MS): void {
    scheduleConsultationDbDrop(ttlMs);
  }

  private createDiagnostics(): RecoveryDiagnostics {
    return new RecoveryDiagnostics(this.db, {
      sessionId: this.sessionId,
      limit: this.config.diagnosticsLimit,
      onEvent: (event) => {
        if (this.options.onDiagnostic) {
          this.options.onDiagnostic({
            sessionId: event.sessionId,
            ts: event.ts,
            code: event.code,
            detail: event.detail,
          });
        }
      },
    });
  }

  private refreshStorageBindings(): void {
    this.db = getConsultationDb();
    this.manifestRepo = new SessionManifestRepo(this.db);
    this.segmentRepo = new AudioSegmentRepo(this.db);
    this.checkpointRepo = new RecoveryCheckpointRepo(this.db);
    this.payloadStore = new IdbPayloadStore(this.db);
    this.retentionManager = new RetentionManager(
      this.segmentRepo,
      this.manifestRepo,
      this.payloadStore
    );
    this.archiveBuilder = new FinalArchiveBuilder(this.payloadStore);
    this.diagnostics = this.createDiagnostics();
  }

  private async ensureSession(): Promise<void> {
    if (!this.manifest) {
      await this.startSession();
    }
  }

  private async resetConsultationStorage(): Promise<void> {
    await dropConsultationDb();
    this.manifest = null;
    this.nextSegmentId = 1;
    this.capturedTimelineMs = 0;
    this.lastFlushAt = null;
    this.appendChain = Promise.resolve();
    this.persistenceQueue = Promise.resolve();
    this.manifestPersistInFlight = null;
    this.manifestPersistRequested = false;
    this.flushInFlight = null;
    this.refreshStorageBindings();
  }

  private async logConsultationStorageReset(): Promise<void> {
    if (!ENABLE_CONSULTATION_STORAGE_RESET_LOGGING) {
      return;
    }

    try {
      const [
        manifests,
        segments,
        payloads,
        recoveryCheckpoints,
        chunkIndex,
        diagnostics,
      ] = await Promise.all([
        this.db.manifests.count(),
        this.db.segments.count(),
        this.db.payloads.count(),
        this.db.recoveryCheckpoints.count(),
        this.db.chunkIndex.count(),
        this.db.diagnostics.count(),
      ]);

      console.log("[ConsultationStorageReset]", {
        sessionId: this.sessionId,
        manifests,
        segments,
        payloads,
        recoveryCheckpoints,
        chunkIndex,
        diagnostics,
      });
    } catch (error) {
      console.warn("[ConsultationStorageReset] Unable to inspect storage after reset.", error);
    }
  }

  private async enqueuePersistenceTask<T>(task: () => Promise<T>): Promise<T> {
    const queuedTask = this.persistenceQueue.then(() =>
      this.runPersistenceTaskWithRecovery(task)
    );
    this.persistenceQueue = queuedTask.then(
      () => undefined,
      () => undefined
    );
    return queuedTask;
  }

  private async runPersistenceTaskWithRecovery<T>(task: () => Promise<T>): Promise<T> {
    try {
      return await task();
    } catch (error) {
      if (!this.isDatabaseClosedError(error)) {
        throw error;
      }

      this.refreshStorageBindings();
      await this.diagnostics.record("durable_db_reopened_after_close", {});
      return task();
    }
  }

  private async persistManifest(): Promise<void> {
    if (!this.manifest) {
      return;
    }

    this.manifestPersistRequested = true;

    if (this.manifestPersistInFlight) {
      await this.manifestPersistInFlight;
      return;
    }

    const persistPromise = this.enqueuePersistenceTask(async () => {
      while (this.manifestPersistRequested) {
        this.manifestPersistRequested = false;
        await this.db.manifests.put({
          ...(this.manifest as SessionManifest),
        });
      }
    });

    this.manifestPersistInFlight = persistPromise;

    try {
      await persistPromise;
    } finally {
      if (this.manifestPersistInFlight === persistPromise) {
        this.manifestPersistInFlight = null;
      }
    }
  }

  private async applyBackpressureIfNeeded(
    incomingByteLength: number,
    now: number
  ): Promise<void> {
    if (!this.flushInFlight) {
      return;
    }

    const projectedPendingBytes =
      this.segmentBuilder.getPendingBytes() + incomingByteLength;

    if (projectedPendingBytes <= this.config.maxPendingBytes) {
      return;
    }

    await this.diagnostics.record("durable_backpressure_wait", {
      projectedPendingBytes,
      maxPendingBytes: this.config.maxPendingBytes,
      pendingBytes: this.segmentBuilder.getPendingBytes(),
      at: now,
    });

    await this.flushInFlight;
  }

  private async estimateBacklogBytes(committedMs: number): Promise<number> {
    const segments = await this.db.segments
      .where("[sessionId+endMs]")
      .between([this.sessionId, committedMs], [this.sessionId, Number.MAX_SAFE_INTEGER])
      .toArray();

    return segments.reduce((total, segment) => total + segment.byteLength, 0);
  }

  private isDatabaseClosedError(error: unknown): boolean {
    if (!error || typeof error !== "object") {
      return false;
    }

    const maybeError = error as { name?: string; inner?: { name?: string } };
    return (
      maybeError.name === "DatabaseClosedError" ||
      maybeError.inner?.name === "DatabaseClosedError"
    );
  }
}
